import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '../config/awsConfig';
import tokenManager from './TokenManager';

// Create axios instance with proper typing
const api: AxiosInstance = axios.create({
  // apiConfig.baseUrl is always defined -- awsConfig.js fails loudly (see
  // task 2.7) rather than falling back to a hardcoded host when the
  // VITE_API_* env vars are missing, so no fallback is needed here.
  baseURL: apiConfig.baseUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Request interceptor - simplified
api.interceptors.request.use(async (config) => {
  // Skip token logic for refresh endpoint
  if (config.url?.includes('refresh-token')) {
    return config;
  }

  const useAuth = import.meta.env.VITE_USE_AUTH === 'true';
  const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';

  // Debug ALL API requests to see what's happening
  const debugInfo = {
    url: config.url,
    method: config.method,
    useAuth,
    isGuestMode,
    sessionStorageGuestMode: sessionStorage.getItem('isGuestMode'),
    allSessionStorageKeys: Object.keys(sessionStorage),
    timestamp: new Date().toISOString()
  };

  console.log('🌐 API Interceptor:', debugInfo);

  // Store in localStorage so it persists through redirects
  const existingLogs = JSON.parse(localStorage.getItem('apiInterceptorLogs') || '[]');
  existingLogs.push(debugInfo);
  localStorage.setItem('apiInterceptorLogs', JSON.stringify(existingLogs.slice(-10))); // Keep last 10 logs

  // Handle guest mode URL modification
  if (isGuestMode && config.url && !config.url.startsWith('/guest')) {
    const originalUrl = config.url;
    config.url = `/guest${config.url}`;

    const modificationLog = {
      action: 'URL_MODIFIED',
      original: originalUrl,
      modified: config.url,
      isGuestMode,
      sessionStorageGuestMode: sessionStorage.getItem('isGuestMode'),
      timestamp: new Date().toISOString()
    };

    console.log('🔄 API URL Modified for guest mode:', modificationLog);
    localStorage.setItem('lastUrlModification', JSON.stringify(modificationLog));

    // Log the EXACT final config that will be sent
    console.log('🚀 FINAL REQUEST CONFIG AFTER MODIFICATION:', {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL,
      headers: config.headers,
      data: config.data
    });
  } else {
    const noModificationLog = {
      action: 'URL_NOT_MODIFIED',
      url: config.url,
      isGuestMode,
      sessionStorageGuestMode: sessionStorage.getItem('isGuestMode'),
      startsWithGuest: config.url ? config.url.startsWith('/guest') : 'no url',
      reason: !isGuestMode ? 'not_guest_mode' : !config.url ? 'no_url' : 'already_guest_url',
      timestamp: new Date().toISOString()
    };

    console.log('🔄 API URL NOT Modified:', noModificationLog);
    localStorage.setItem('lastUrlModification', JSON.stringify(noModificationLog));
  }

  // Handle authentication for non-guest requests (only if auth is enabled)
  if (!isGuestMode && useAuth) {
    // Check if token needs refresh
    const refreshCheck = tokenManager.needsRefresh();
    if (refreshCheck.needed) {
      try {
        await tokenManager.refreshTokens();
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
        return Promise.reject(new Error('Authentication failed'));
      }
    }

    // Add current token to request
    const idToken = tokenManager.getIdToken();
    if (idToken && config.headers) {
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
  } else {
    // Handle guest mode headers
    const guestCredentials = sessionStorage.getItem('guestCredentials');
    if (guestCredentials) {
      try {
        const credentials = JSON.parse(guestCredentials);
        if (credentials.token && credentials.userId && credentials.rootUrl) {
          config.headers['X-Canvas-Token'] = credentials.token;
          config.headers['X-Canvas-User-Id'] = credentials.userId;
          config.headers['X-Canvas-Root-Url'] = credentials.rootUrl;
        }
      } catch (e) {
        console.error('Failed to parse guest credentials:', e);
      }
    }
  }

  // Log the FINAL config being returned from interceptor
  console.log('📤 FINAL CONFIG LEAVING INTERCEPTOR:', {
    url: config.url,
    method: config.method,
    baseURL: config.baseURL,
    fullUrl: `${config.baseURL || ''}${config.url}`,
    timestamp: new Date().toISOString()
  });

  // Store final config in localStorage
  localStorage.setItem('lastFinalConfig', JSON.stringify({
    url: config.url,
    method: config.method,
    baseURL: config.baseURL,
    fullUrl: `${config.baseURL || ''}${config.url}`,
    timestamp: new Date().toISOString()
  }));

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor - simplified
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    // Log API errors for debugging
    if (import.meta.env.DEV || error.response?.status === 403) {
      console.error('🚨 API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.message
      });
    }

    const originalRequest = error.config;
    
    // Handle network errors
    if (!error.response) {
      const tokenInfo = await tokenManager.getTokenInfo();
      if (!tokenInfo.hasValidTokens) {
        tokenManager.forceLogout();
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
      return Promise.reject(error);
    }
    
    // Handle 401 errors
    if (error.response.status === 401 && !originalRequest._retry) {
      // Don't refresh for Canvas API calls
      if (originalRequest.headers?.['X-Canvas-Token']) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      
      try {
        const refreshResult = await tokenManager.refreshTokens();
        
        // Retry request with new token
        originalRequest.headers['Authorization'] = `Bearer ${refreshResult.idToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        tokenManager.forceLogout();
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;